import { useEffect, useState } from 'react';
import type { Conversation, ConversationListEntry } from '../../types';
import { api } from '../../api';
import { useChat } from '../../store';
import { IconArrowLeft, IconClose } from '../Icon';
import type { ActiveCallInfo } from '../CallModal';
import { InfoMain } from './InfoMain';
import { InfoMembers } from './InfoMembers';
import { InfoMedia } from './InfoMedia';
import { InfoStarred } from './InfoStarred';
import { InfoEncryption } from './InfoEncryption';

export type InfoSection = 'main' | 'members' | 'media' | 'starred' | 'encryption';

interface ConversationInfoPanelProps {
  conversation: ConversationListEntry;
  onClose: () => void;
  onLeft: () => void;
  onStartCall: (info: ActiveCallInfo) => void;
  onOpenSearch: () => void;
}

export function ConversationInfoPanel({
  conversation,
  onClose,
  onLeft,
  onStartCall,
  onOpenSearch,
}: ConversationInfoPanelProps) {
  const [section, setSection] = useState<InfoSection>('main');
  const [details, setDetails] = useState<Conversation | null>(null);
  const applyConversationUpdate = useChat((s) => s.applyConversationUpdate);

  // Reset to main when the conversation changes.
  useEffect(() => {
    setSection('main');
    setDetails(null);
  }, [conversation.id]);

  // Fetch fresh conversation details (mutedUntil, disappearingSeconds, etc.).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.conversation(conversation.id);
        if (!cancelled) setDetails(res.conversation);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation.id]);

  // Esc to close — outermost handler so sub-panels also benefit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (section === 'main') onClose();
        else setSection('main');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, section]);

  const title =
    section === 'main'
      ? conversation.type === 'dm'
        ? 'Contact info'
        : 'Group info'
      : section === 'members'
      ? 'Members'
      : section === 'media'
      ? 'Media, links and docs'
      : section === 'starred'
      ? 'Starred messages'
      : 'Encryption';

  function handleConversationDetailsUpdate(next: Partial<Conversation>) {
    setDetails((d) => (d ? { ...d, ...next } : d));
    if (
      next.name !== undefined ||
      next.topic !== undefined ||
      next.avatarUpdatedAt !== undefined ||
      next.mutedUntil !== undefined
    ) {
      applyConversationUpdate(conversation.id, {
        name: next.name,
        topic: next.topic,
        avatarUpdatedAt: next.avatarUpdatedAt,
        mutedUntil: next.mutedUntil,
      });
    }
  }

  return (
    <aside className="w-[22rem] hairline bg-zinc-950/95 backdrop-blur-xl flex flex-col shrink-0 min-w-0 animate-fade-up">
      <header className="sticky top-0 z-10 glass-chrome hairline px-4 py-3 flex items-center gap-2 shrink-0">
        {section !== 'main' ? (
          <button
            onClick={() => setSection('main')}
            className="group w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
            aria-label="Back"
          >
            <IconArrowLeft size={15} className="transition-transform duration-300 ease-spring group-hover:-translate-x-0.5" />
          </button>
        ) : null}
        <h2 className="text-sm font-semibold tracking-tight flex-1 truncate">{title}</h2>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
          aria-label="Close"
        >
          <IconClose size={13} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        {section === 'main' && (
          <InfoMain
            conversation={conversation}
            details={details}
            onNavigate={setSection}
            onConversationUpdate={handleConversationDetailsUpdate}
            onLeft={onLeft}
            onStartCall={onStartCall}
            onOpenSearch={onOpenSearch}
          />
        )}
        {section === 'members' && (
          <InfoMembers conversation={conversation} onLeft={onLeft} />
        )}
        {section === 'media' && <InfoMedia conversation={conversation} />}
        {section === 'starred' && (
          <InfoStarred conversation={conversation} onJumped={onClose} />
        )}
        {section === 'encryption' && <InfoEncryption conversation={conversation} />}
      </div>
    </aside>
  );
}
